/**
 * #436 水墨涟漪效果 (Ink Ripple Effect)
 * 
 * 功能特点：
 * 1. 优雅的东方水墨风格
 * 2. 多层涟漪扩散动画
 * 3. 水墨晕染效果
 * 4. 粒子流动轨迹
 * 
 * 技术实现：
 * - SVG 滤镜实现水墨纹理
 * - 数学函数控制涟漪扩散
 * - Spring 动画实现自然过渡
 * - 分层渲染优化性能
 * 
 * 创建时间：2026-03-07
 * 适用场景：东方美学视频、文化宣传、禅意视频
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 涟漪数据结构
interface RippleData {
  x: number;
  y: number;
  startTime: number;
  maxRadius: number;
  opacity: number;
}

export interface InkRippleEffectProps {
  /** 主文字内容 */
  text?: string;
  /** 印章文字 */
  stampText?: string;
  /** 宣纸背景色 */
  backgroundColor?: string;
  /** 水墨颜色 (r, g, b) */
  inkColor?: [number, number, number];
  /** 印章颜色 (r, g, b) */
  stampColor?: [number, number, number];
  /** 涟漪数量 */
  rippleCount?: number;
  /** 粒子数量 */
  particleCount?: number;
  /** 文字字号 */
  fontSize?: number;
  /** 字体 */
  fontFamily?: string;
}

const DEFAULT_INK_COLOR: [number, number, number] = [20, 20, 30];
const DEFAULT_STAMP_COLOR: [number, number, number] = [180, 40, 40];

// 生成随机涟漪
const generateRipples = (count: number, duration: number): RippleData[] => {
  const ripples: RippleData[] = [];
  for (let i = 0; i < count; i++) {
    ripples.push({
      x: 0.2 + random(`ripple-x-${i}`) * 0.6,
      y: 0.2 + random(`ripple-y-${i}`) * 0.6,
      startTime: random(`ripple-start-${i}`) * duration * 0.6,
      maxRadius: 100 + random(`ripple-radius-${i}`) * 150,
      opacity: 0.3 + random(`ripple-opacity-${i}`) * 0.4,
    });
  }
  return ripples;
};

// 单个涟漪组件
const InkRipple: React.FC<{
  ripple: RippleData;
  frame: number;
  fps: number;
  width: number;
  height: number;
  inkColor: [number, number, number];
}> = ({ ripple, frame, fps, width, height, inkColor }) => {
  const relativeFrame = frame - ripple.startTime;
  
  if (relativeFrame < 0) return null;

  const progress = interpolate(relativeFrame, [0, 60], [0, 1], {
    extrapolateRight: 'clamp',
  });

  const radius = ripple.maxRadius * progress;
  const opacity = ripple.opacity * (1 - progress * 0.8);
  const strokeWidth = interpolate(progress, [0, 1], [3, 0.5]);

  const centerX = ripple.x * width;
  const centerY = ripple.y * height;
  const [r, g, b] = inkColor;

  return (
    <circle
      cx={centerX}
      cy={centerY}
      r={radius}
      fill="none"
      stroke={`rgba(${r}, ${g}, ${b}, ${opacity})`}
      strokeWidth={strokeWidth}
      style={{
        filter: 'url(#ink-blur)',
      }}
    />
  );
};

// 水墨粒子
const InkParticle: React.FC<{
  index: number;
  frame: number;
  fps: number;
  width: number;
  height: number;
  inkColor: [number, number, number];
}> = ({ index, frame, fps, width, height, inkColor }) => {
  const seed = random(`particle-${index}`);
  const startX = random(`particle-start-x-${index}`) * width;
  const startY = height + 50;
  
  const speed = 1 + seed * 2;
  const progress = (frame * speed) % 120;
  
  const x = startX + Math.sin(frame * 0.02 + seed * 10) * 50;
  const y = startY - progress * 3;
  const size = 2 + seed * 4;
  const opacity = interpolate(progress, [0, 60, 120], [0, 0.6, 0]);
  const [r, g, b] = inkColor;

  if (y < -50) return null;

  return (
    <circle
      cx={x}
      cy={y}
      r={size}
      fill={`rgba(${r}, ${g}, ${b}, ${opacity})`}
      style={{
        filter: 'url(#ink-blur)',
      }}
    />
  );
};

// 水墨文字
const InkText: React.FC<{
  frame: number;
  fps: number;
  width: number;
  height: number;
  text: string;
  fontSize: number;
  fontFamily: string;
  inkColor: [number, number, number];
}> = ({ frame, fps, width, height, text, fontSize, fontFamily, inkColor }) => {
  const chars = text.split('');
  const [r, g, b] = inkColor;
  
  const overallProgress = spring({
    frame,
    fps,
    config: {
      damping: 100,
      stiffness: 200,
      mass: 0.5,
    },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        gap: '40px',
        fontFamily,
        fontSize: `${fontSize}px`,
        color: `rgba(${r}, ${g}, ${b}, 0.85)`,
        textShadow: `0 4px 20px rgba(${r}, ${g}, ${b}, 0.3)`,
        filter: 'url(#ink-texture)',
      }}
    >
      {chars.map((char, i) => {
        const charDelay = i * 8;
        const charProgress = spring({
          frame: frame - charDelay,
          fps,
          config: {
            damping: 80,
            stiffness: 150,
            mass: 0.8,
          },
        });

        const opacity = interpolate(charProgress, [0, 0.3, 1], [0, 0.3, 1]);
        const translateY = interpolate(charProgress, [0, 1], [30, 0]);
        const scale = interpolate(charProgress, [0, 0.5, 1], [0.8, 1.05, 1]);

        return (
          <div
            key={i}
            style={{
              opacity,
              transform: `translateY(${translateY}px) scale(${scale})`,
              filter: `blur(${interpolate(charProgress, [0, 1], [4, 0])}px)`,
            }}
          >
            {char}
          </div>
        );
      })}
    </div>
  );
};

// 主组件
export const InkRippleEffect: React.FC<InkRippleEffectProps> = ({
  text = '静水流深',
  stampText = '静',
  backgroundColor = '#F5F1E8',
  inkColor = DEFAULT_INK_COLOR,
  stampColor = DEFAULT_STAMP_COLOR,
  rippleCount = 8,
  particleCount = 30,
  fontSize = 120,
  fontFamily = 'STKaiti, KaiTi, serif',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const ripples = generateRipples(rippleCount, 150);
  const [ir, ig, ib] = inkColor;
  const [sr, sg, sb] = stampColor;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* SVG 滤镜定义 */}
      <svg
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
        }}
      >
        <defs>
          {/* 水墨模糊效果 */}
          <filter id="ink-blur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
          </filter>
          
          {/* 水墨纹理效果 */}
          <filter id="ink-texture">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.04"
              numOctaves="3"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="3"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* 涟漪层 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {ripples.map((ripple, i) => (
          <InkRipple
            key={i}
            ripple={ripple}
            frame={frame}
            fps={fps}
            width={width}
            height={height}
            inkColor={inkColor}
          />
        ))}
      </svg>

      {/* 粒子层 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {Array.from({ length: particleCount }).map((_, i) => (
          <InkParticle
            key={i}
            index={i}
            frame={frame}
            fps={fps}
            width={width}
            height={height}
            inkColor={inkColor}
          />
        ))}
      </svg>

      {/* 文字层 */}
      <InkText
        frame={frame}
        fps={fps}
        width={width}
        height={height}
        text={text}
        fontSize={fontSize}
        fontFamily={fontFamily}
        inkColor={inkColor}
      />

      {/* 装饰性印章 */}
      <div
        style={{
          position: 'absolute',
          bottom: '100px',
          right: '150px',
          width: '80px',
          height: '80px',
          border: `3px solid rgba(${sr}, ${sg}, ${sb}, 0.8)`,
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily,
          fontSize: '36px',
          color: `rgba(${sr}, ${sg}, ${sb}, 0.8)`,
          opacity: spring({
            frame: frame - 60,
            fps,
            config: { damping: 100, stiffness: 200 },
          }),
          transform: `rotate(${interpolate(
            spring({
              frame: frame - 60,
              fps,
              config: { damping: 100, stiffness: 200 },
            }),
            [0, 1],
            [-10, 0]
          )}deg)`,
        }}
      >
        {stampText}
      </div>

      {/* 水墨晕染背景装饰 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0.15,
        }}
      >
        <ellipse
          cx={width * 0.2}
          cy={height * 0.3}
          rx={150 + Math.sin(frame * 0.02) * 20}
          ry={100 + Math.cos(frame * 0.02) * 15}
          fill={`rgba(${ir}, ${ig}, ${ib}, 0.4)`}
          filter="url(#ink-blur)"
        />
        <ellipse
          cx={width * 0.8}
          cy={height * 0.7}
          rx={180 + Math.cos(frame * 0.015) * 25}
          ry={120 + Math.sin(frame * 0.015) * 20}
          fill={`rgba(${ir}, ${ig}, ${ib}, 0.3)`}
          filter="url(#ink-blur)"
        />
      </svg>
    </AbsoluteFill>
  );
};
